import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import FormDataJson from 'form-data-json-convert';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Slideshow from './Slideshow';
import ProductImageView from './Products/ProductImageView';
import Attributes from './Attributes';
import Addons from './Addons';
import Warning from './Warning';
import DoubleSidedHeadSelector from './DoubleSidedHeadSelector';
import HeadBuilderOptions from './HeadBuilderOptions';
import BuilderMoney from '../BuilderMoney';

import { isKeychainProductId } from '../../constants';
import { setTorsoText as setTorsoTextAction, setTorsoOverlay, removeProduct } from '../../actions';
import {
    deriveDefaultVariant,
    computeTorsoBackImage,
    computeTorsoOverlayImages,
    resolveReverseHeadImage,
    computeProductPrice,
    computeInStock,
} from '../../utils/customiseHelpers';
import { convertFileToBase64 } from '../../wordpress';
import { checkIncompatibilities, formatIncompatibilityAlert } from '../../utils/incompatibility';
import { getDisplayUploadConfig } from '../../utils/displayUploadConfig';

/**
 * Customise product screen
 *
 * Allows for entering details for the product
 *
 * @author John Noel <john.noel@joipolloi.com>
 * @author Ben Ghabili <ben.ghabili@joipolloi.com>
 * @author Matt Haynes <Matthew.haynes@joipolloi.com>
 * @package Minifigs
 */

const Customise = ({
    projectTitle = '',
    product,
    selected = null,
    onSelect = () => {},
    onRemove = () => {},
    backButtonFunc = () => {},
    activeSection = '',
    currentBuildProducts = {},
    skintone = '',
    headFeatures = {},
    showDescription = false,
    setShowDescription = () => {},
    dispatch = () => {},
    currentBuildIndex,
    torsoReduxText = { frontText: '', backText: '' },
    torsoImageUpload = null,
    displayImageUpload = null,
    displayImageUploadDraft = null,
}) => {
    const defaultVariantRef = useRef(null);
    if (!defaultVariantRef.current) {
        defaultVariantRef.current = deriveDefaultVariant({
            product,
            skintone,
            activeSection,
        });
    }
    const defaultVariant = defaultVariantRef.current;
    const [attributes, setAttributes] = useState(
        selected && selected.attributes !== null ? selected.attributes : defaultVariant.attr
    );
    const [addons] = useState(selected !== null ? product?.product_attribute || [] : []);
    const [showingCopyright, setShowingCopyright] = useState(false);
    const [dropdownDefaultValue, setDropdownDefaultValue] = useState(defaultVariant.id);
    const [productMatchAttr, setProductMatchAttr] = useState(defaultVariant.attr);
    const [, setProductFormJsonValues] = useState(null);
    const [frontTextEventListenerExists, setFrontTextEventListenerExists] = useState(false);
    const [backTextEventListenerExists, setBackTextEventListenerExists] = useState(false);
    const [torsoOverlayImages, setTorsoOverlayImages] = useState({
        handImage: '',
        torsoOverlayImage: '',
    });
    const [torsoText, setTorsoTextState] = useState({
        frontText: '',
        backText: '',
    });
    const [torsoBackImage, setTorsoBackImage] = useState(null);
    const [showBackImage, setShowBackImage] = useState(false);
    const [showReplaceItemWarning, setShowReplaceItemWarning] = useState(false);
    const [formToSubmit, setFormToSubmit] = useState(null);
    const [videoUrls, setVideoUrls] = useState([]);
    const [showCompatWarning, setShowCompatWarning] = useState(false);
    const [compatWarningText, setCompatWarningText] = useState('');
    const [compatWarningTitle, setCompatWarningTitle] = useState('');
    const [compatDeferredForm, setCompatDeferredForm] = useState(null);
    const overlayObserverRef = useRef(null);
    const overlayCleanupRef = useRef(null);
    const addonsContainerRef = useRef(null);
    const addonOptionSetsRef = useRef([]);

    const productPrice = computeProductPrice(product, attributes);
    const productStockDisplay = false;

    const isKeyChain = isKeychainProductId(product?.id);

    const attributesVars = product.variants.nodes ? product.variants.nodes : product.variants.edges;
    const reverseImage = resolveReverseHeadImage(activeSection, attributes, skintone);

    const selectedHeadProduct = currentBuildProducts?.heads?.product || null;
    const selectedKeychainProduct =
        currentBuildProducts?.keychain?.product || currentBuildProducts?.display?.product || null;

    const MAX_DESCRIPTION_CHARS = 190;

    const cleanupOverlayBindings = useCallback(() => {
        if (overlayObserverRef.current) {
            overlayObserverRef.current.disconnect();
            overlayObserverRef.current = null;
        }

        if (overlayCleanupRef.current) {
            overlayCleanupRef.current();
            overlayCleanupRef.current = null;
        }
    }, []);

    const updateTorsoOverlayImages = useCallback(() => {
        const overlayImages = computeTorsoOverlayImages(product, attributes, skintone);
        setTorsoOverlayImages(overlayImages);
    }, [attributes, product, skintone]);

    const attachBackCheckboxListener = useCallback(() => {
        if (activeSection !== 'bodies') {
            cleanupOverlayBindings();
            return;
        }

        const container = addonsContainerRef.current;
        if (!container) {
            cleanupOverlayBindings();
            return;
        }

        cleanupOverlayBindings();

        const findCheckbox = () =>
            container.querySelector(
                'input[type="checkbox"] + input[type="hidden"][name="properties[Add text to the back?]"]'
            )?.previousElementSibling;

        const bindCheckbox = (checkbox) => {
            if (!checkbox) {
                return false;
            }

            const handleChange = (e) => {
                if (e.target.checked) {
                    setShowBackImage(true);
                }
            };

            checkbox.addEventListener('change', handleChange);
            overlayCleanupRef.current = () => {
                checkbox.removeEventListener('change', handleChange);
            };

            return true;
        };

        if (bindCheckbox(findCheckbox())) {
            return;
        }

        const observer = new MutationObserver(() => {
            const checkbox = findCheckbox();

            if (!checkbox) {
                return;
            }

            if (bindCheckbox(checkbox) && overlayObserverRef.current) {
                overlayObserverRef.current.disconnect();
                overlayObserverRef.current = null;
            }
        });

        observer.observe(container, {
            childList: true,
            subtree: true,
        });

        overlayObserverRef.current = observer;
    }, [activeSection, cleanupOverlayBindings]);

    const setBackImage = useCallback(() => {
        const backImage = computeTorsoBackImage(product, attributes, skintone);
        setTorsoBackImage(backImage);
    }, [attributes, product, skintone]);

    const changeAttribute = (attribute, selectedValue) => {
        setDropdownDefaultValue(selectedValue);
        setProductMatchAttr(attribute);
        setAttributes(attribute);

        if (activeSection === 'bodies') {
            const overlayImages = computeTorsoOverlayImages(product, attribute, skintone);
            setTorsoOverlayImages(overlayImages);
            const backImage = computeTorsoBackImage(product, attribute, skintone);
            setTorsoBackImage(backImage);
            attachBackCheckboxListener();
        }
    };

    const submitProductForm = async (clickedButtonForm, options = {}) => {
        const { skipCompatCheck = false } = options;
        if (activeSection === 'accessories') {
            const accessoriesArr = Array.isArray(currentBuildProducts?.accessories)
                ? currentBuildProducts.accessories
                : [];
            const alreadyExists = accessoriesArr.some((acc) => acc?.product?.id === product.id);
            if (alreadyExists) {
                return true;
            }
        }
        const windowAddonSets = typeof window !== 'undefined' && Array.isArray(window.addonArr) ? window.addonArr : [];
        const addonOptionSets = addonOptionSetsRef.current;
        const effectiveAddonSets =
            Array.isArray(addonOptionSets) && addonOptionSets.length > 0 ? addonOptionSets : windowAddonSets;
        const hasAddonConfig = Array.isArray(effectiveAddonSets) && effectiveAddonSets.length > 0;
        const addonScope = addonsContainerRef.current || clickedButtonForm || document;
        const getBoldOptionSets = () => Array.from(addonScope.querySelectorAll('.bold_option_set'));

        let validationFailed = false;

        if (!skipCompatCheck) {
            try {
                const incompat = checkIncompatibilities({
                    currentBuild: { selected: currentBuildProducts },
                    candidateProduct: product,
                    candidateSection: activeSection,
                });
                if (incompat) {
                    const msg = formatIncompatibilityAlert(incompat);
                    if (msg.message) {
                        setShowCompatWarning(true);
                        setCompatWarningText(msg.message);
                        setCompatWarningTitle(msg.title);
                        setCompatDeferredForm(clickedButtonForm);
                        return false;
                    }
                }
            } catch (e) {
                console.warn('Customise incompatibility check failed', e);
            }
        }

        const allRequiredFromConfig = [];
        if (hasAddonConfig) {
            effectiveAddonSets.forEach((set) => {
                const setOptions = Array.isArray(set?.options) ? set.options : [];
                setOptions.forEach((opt) => {
                    if (opt?.required) {
                        allRequiredFromConfig.push(opt);
                    }
                });
            });
        }

        // Deferred file upload actions — only committed after ALL validation passes
        const deferredFileActions = [];

        if (hasAddonConfig) {
            const boldOptionSets = getBoldOptionSets();

            boldOptionSets.forEach((set, i) => {
                const opts = Array.from(set.querySelectorAll('.bold_option'));
                console.log(`[Addon Validation] Set ${i}: ${opts.length} options`, {
                    setHTML: set.innerHTML.substring(0, 300),
                    optionTitles: opts.map((o) => (o.querySelector('.bold_option_title')?.textContent || '').trim()),
                    visible: set.offsetParent !== null,
                });
            });

            if (boldOptionSets.length === 0) {
                console.warn('Customise addon validation skipped: addon config present but no option sets found');
                return false;
            }

            for (let index = 0; index < boldOptionSets.length; index += 1) {
                const optionSet = boldOptionSets[index];
                const options = Array.from(optionSet.querySelectorAll('.bold_option'));
                const addonArrValues = Array.isArray(effectiveAddonSets[index]?.options)
                    ? effectiveAddonSets[index].options
                    : [];

                let optionSetFailed = false;

                for (let idx = 0; idx < options.length; idx += 1) {
                    const option = options[idx];
                    const optionConfig = addonArrValues[idx];

                    const optionTitle = (option.querySelector('.bold_option_title')?.textContent || '').trim();
                    const isHidden = option.offsetParent === null;

                    // Try to find config by name if index-based lookup missed
                    let resolvedConfig = optionConfig;
                    if (!resolvedConfig && optionTitle) {
                        const cleanTitle = optionTitle.replace(/\*$/, '').trim();
                        resolvedConfig = allRequiredFromConfig.find(
                            (c) =>
                                (c.public_name || c.internal_name || '').toLowerCase().trim() ===
                                cleanTitle.toLowerCase()
                        );
                    }

                    if (isHidden) {
                        continue;
                    }

                    let optSetValLength = 0;
                    let passedValidation = true;

                    const textareaInput = option.querySelector('textarea');
                    const fileInput = option.querySelector('input[type=file]');
                    const checkboxInput = option.querySelector('input[type="checkbox"]');
                    const textInput = option.querySelector(
                        'input:not([type="file"]):not([type="checkbox"]):not([type="hidden"])'
                    );
                    const selectInput = option.querySelector('select');
                    const titleMarksRequired = optionTitle.includes('*');
                    const isRequiredOption =
                        Boolean(resolvedConfig?.required) ||
                        titleMarksRequired ||
                        Boolean(
                            textareaInput?.required ||
                            fileInput?.required ||
                            checkboxInput?.required ||
                            textInput?.required ||
                            selectInput?.required
                        );

                    if (textareaInput) {
                        optSetValLength = textareaInput.value.replace(/\s/g, '').length;
                        passedValidation = !isRequiredOption || optSetValLength > 0;
                    } else if (fileInput) {
                        optSetValLength = fileInput.value.length;
                        passedValidation = !isRequiredOption || optSetValLength > 0;

                        const input = fileInput;
                        const file = fileInput.files?.[0];
                        const regex = /\[(.*?)\]/;
                        const match = regex.exec(input.name);
                        const name = match ? match[1] : input.name;

                        // Defer file processing — only commit after ALL validation passes
                        if (passedValidation && file) {
                            deferredFileActions.push({ file, name });
                        }
                    } else if (checkboxInput) {
                        const checkboxInputs = Array.from(option.querySelectorAll('input[type="checkbox"]'));
                        const hasCheckedValue = checkboxInputs.some((input) => input.checked);
                        passedValidation = !isRequiredOption || hasCheckedValue;
                        optSetValLength = passedValidation ? 1 : 0;
                    } else if (textInput) {
                        optSetValLength = textInput.value.replace(/\s/g, '').length;
                        passedValidation = !isRequiredOption || optSetValLength > 0;
                    } else if (selectInput) {
                        optSetValLength = selectInput.value.length;
                        passedValidation = !isRequiredOption || optSetValLength > 0;
                    } else if (option.querySelector('file')) {
                        optSetValLength = option.querySelector('file').value.length;
                        passedValidation = !isRequiredOption || optSetValLength > 0;
                    }

                    if (!passedValidation) {
                        optionSet.classList.add('validation-error');
                        optionSet.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        optionSetFailed = true;
                        validationFailed = true;
                    }
                }

                if (optionSetFailed) {
                    optionSet.classList.add('validation-error');
                    optionSet.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    validationFailed = true;
                } else {
                    optionSet.classList.remove('validation-error');
                }
            }
        }

        if (validationFailed && hasAddonConfig) {
            return false;
        }

        const newProductFormJsonValues = FormDataJson.toJson(clickedButtonForm);
        setProductFormJsonValues(newProductFormJsonValues);

        // All validation passed — convert and commit every upload as one update.
        // Calling onSelect from a state updater for each file is unsafe: the first
        // selection can unmount this screen before React runs the remaining updaters.
        if (deferredFileActions.length > 0) {
            const encodedFiles = await Promise.all(
                deferredFileActions.map(async ({ file, name }) => ({
                    name,
                    encodedImage: await convertFileToBase64(file),
                }))
            );
            const uploadedProperties = encodedFiles.reduce(
                (properties, { name, encodedImage }) => ({
                    ...properties,
                    [name]: encodedImage,
                }),
                {}
            );

            const extraUploadProperties = {};

            if (activeSection === 'bodies') {
                dispatch(setTorsoOverlay(currentBuildIndex, torsoOverlayImages));
                dispatch(
                    setTorsoTextAction(currentBuildIndex, {
                        frontText: torsoText.frontText,
                        backText: torsoText.backText,
                    })
                );

                if (uploadedProperties['Image 1'] && torsoImageUpload?.compositeDataUri) {
                    extraUploadProperties['Composite Preview'] = torsoImageUpload.compositeDataUri;
                }
            }

            // Display composite + source image fallback (config-driven)
            if (activeSection === 'display') {
                const dConfig = getDisplayUploadConfig(product?.id);
                const committedUploadMatchesProduct = displayImageUpload?.productId === product?.id;
                const legacyCommittedUploadMatchesProduct =
                    !displayImageUpload?.productId && currentBuildProducts?.display?.product?.id === product?.id;
                const displayUploadForProduct =
                    displayImageUploadDraft?.productId === product?.id
                        ? displayImageUploadDraft
                        : committedUploadMatchesProduct || legacyCommittedUploadMatchesProduct
                          ? displayImageUpload
                          : null;

                if (dConfig && displayUploadForProduct?.compositeDataUri) {
                    extraUploadProperties[dConfig.cartPropertyName] = displayUploadForProduct.compositeDataUri;
                }

                if (dConfig && displayUploadForProduct?.dataUri) {
                    const expectedNames = (dConfig.inputSelectors || [])
                        .map((selector) => {
                            const match = selector.match(/\[name="properties\[([^\]]+)\]"\]/);
                            return match ? match[1] : null;
                        })
                        .filter(Boolean);
                    const hasSourceImage = expectedNames.some((name) => {
                        const value = uploadedProperties[name] || newProductFormJsonValues?.properties?.[name];
                        return typeof value === 'string' && value.startsWith('data:');
                    });

                    if (!hasSourceImage && expectedNames.length > 0) {
                        extraUploadProperties[expectedNames[0]] = displayUploadForProduct.dataUri;
                    }
                }
            }

            const updatedForm = {
                ...newProductFormJsonValues,
                properties: {
                    ...(newProductFormJsonValues?.properties || {}),
                    ...uploadedProperties,
                    ...extraUploadProperties,
                },
            };

            setProductFormJsonValues(updatedForm);
            onSelect(product, attributes, addons, productMatchAttr, updatedForm);
        }

        const warnings = document.querySelectorAll('.please-choose-warning');
        const formAttributes = document.querySelector('.attributes .product-variation');
        if (warnings.length > 0) {
            if (dropdownDefaultValue === '') {
                warnings.forEach((warning) => {
                    warning.style.display = 'inline-block';
                });
                formAttributes.scrollIntoView({ behavior: 'smooth' });
            } else {
                warnings.forEach((warning) => {
                    warning.style.display = 'none';
                });
            }
        }

        let hasFileField = false;
        if (hasAddonConfig) {
            const boldOptionSets = getBoldOptionSets();
            boldOptionSets.forEach((optionSet) => {
                const options = Array.from(optionSet.querySelectorAll('.bold_option'));
                options.forEach((option) => {
                    if (option.querySelector('input[type=file]')) {
                        hasFileField = option.querySelector('input[type=file]');
                    }
                });
            });
        }

        const productNodes = product.variants.nodes ? product.variants.nodes : product.variants.edges;
        if (!hasFileField) {
            for (let i = 0, n = productNodes.length; i < n; i += 1) {
                const nodeWrapper = productNodes[i];
                const productId = nodeWrapper?.node
                    ? nodeWrapper?.node.id.split('/').pop()
                    : nodeWrapper?.id.split('/').pop();
                if (productId == dropdownDefaultValue && nodeWrapper !== 'undefined') {
                    const matchedAttr = nodeWrapper?.node ? nodeWrapper.node : nodeWrapper;
                    setProductMatchAttr(matchedAttr);

                    if (activeSection === 'bodies') {
                        dispatch(setTorsoOverlay(currentBuildIndex, torsoOverlayImages));
                        dispatch(
                            setTorsoTextAction(currentBuildIndex, {
                                frontText: torsoText.frontText,
                                backText: torsoText.backText,
                            })
                        );
                    }

                    onSelect(product, attributes, addons, matchedAttr, newProductFormJsonValues);
                }
            }
        } else if (!hasAddonConfig) {
            const firstVariant = product.variants.nodes ? product.variants.nodes[0] : product.variants.edges[0].node;
            setProductMatchAttr(firstVariant);

            if (activeSection === 'bodies') {
                dispatch(setTorsoOverlay(currentBuildIndex, torsoOverlayImages));

                dispatch(
                    setTorsoTextAction(currentBuildIndex, {
                        frontText: torsoText.frontText,
                        backText: torsoText.backText,
                    })
                );
            }

            onSelect(product, attributes, addons, firstVariant, newProductFormJsonValues);
        }
    };

    const { collapsedText, needsTruncate } = useMemo(() => {
        const html = product?.descriptionHtml || '';
        if (!html) {
            return { collapsedText: '', needsTruncate: false };
        }
        let fullText = '';
        if (typeof window !== 'undefined') {
            const el = document.createElement('div');
            el.innerHTML = html;
            fullText = el.textContent || el.innerText || '';
        } else {
            fullText = html.replace(/<[^>]*>/g, '');
        }
        const trimmed = fullText.trim();
        if (trimmed.length <= MAX_DESCRIPTION_CHARS) {
            return { collapsedText: trimmed, needsTruncate: false };
        }
        return {
            collapsedText: trimmed.substring(0, MAX_DESCRIPTION_CHARS).trimEnd() + '…',
            needsTruncate: true,
        };
    }, [product?.descriptionHtml]);

    useEffect(() => {
        const urls =
            product?.media?.edges
                ?.filter((edge) => edge.node.mediaContentType === 'VIDEO')
                ?.map((edge) => edge.node.sources[0]) || [];

        setVideoUrls(urls);
    }, [product]);

    useEffect(() => {
        return () => {
            cleanupOverlayBindings();
        };
    }, [cleanupOverlayBindings]);

    useEffect(() => {
        const optionSets = product?.product_attribute?.option_product?.option_sets;
        if (Array.isArray(optionSets)) {
            addonOptionSetsRef.current = optionSets;
        }
    }, [product]);

    useEffect(() => {
        const sectionProduct = currentBuildProducts?.[activeSection] || null;
        const sectionHasProduct = activeSection === 'accessories' ? false : sectionProduct !== null;

        if (sectionHasProduct && activeSection === 'bodies' && sectionProduct?.product?.id === product.id) {
            setTorsoTextState((prev) => ({
                ...prev,
                frontText: torsoReduxText.frontText,
                backText: torsoReduxText.backText,
            }));
        }

        if (activeSection === 'bodies') {
            setBackImage();
            updateTorsoOverlayImages();
            attachBackCheckboxListener();
        } else {
            cleanupOverlayBindings();
        }
    }, [
        activeSection,
        product,
        currentBuildProducts,
        setBackImage,
        updateTorsoOverlayImages,
        attachBackCheckboxListener,
        cleanupOverlayBindings,
        torsoReduxText,
    ]);

    useEffect(() => {
        if (activeSection === 'bodies') {
            setBackImage();
            updateTorsoOverlayImages();
            attachBackCheckboxListener();
        }
    }, [skintone, activeSection, setBackImage, updateTorsoOverlayImages, attachBackCheckboxListener]);

    const sectionProduct = currentBuildProducts?.[activeSection];
    let sectionHasProduct = false;
    if (activeSection === 'accessories') {
        sectionHasProduct = false;
    } else if (activeSection === 'display') {
        sectionHasProduct = !isKeyChain && sectionProduct !== null && sectionProduct !== undefined;
    } else {
        sectionHasProduct = sectionProduct !== null && sectionProduct !== undefined;
    }

    let isSelected = selected && selected?.product?.id === product.id;
    let accessoryRemoveSectionId = null;
    let accessoryIndex = -1;
    if (activeSection === 'accessories') {
        const accessoriesArr = Array.isArray(currentBuildProducts?.accessories) ? currentBuildProducts.accessories : [];
        accessoryIndex = accessoriesArr.findIndex((acc) => acc?.product?.id === product.id);
        if (accessoryIndex !== -1) {
            isSelected = true;
            accessoryRemoveSectionId = `accessories:${accessoryIndex}`;
        }
    }

    const prodIdParsed = product && product.id?.split('/').pop();

    const isInStock = computeInStock(product);

    let button = (
        <button
            type="button"
            className="btn btn-pill product-buy"
            onClick={(e) => {
                if (sectionHasProduct) {
                    setShowReplaceItemWarning(true);
                    setFormToSubmit(e.target.form);
                } else {
                    submitProductForm(e.target.form);
                }
                // const previewSection = document.querySelector('.preview-section');
                // if (previewSection) {
                //     previewSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                // }
            }}
        >
            <span>Add piece</span>
        </button>
    );

    if (isSelected) {
        button = (
            <button
                type="button"
                className="btn btn-pill inverse product-buy product-remove"
                onClick={(e) => {
                    if (activeSection === 'accessories') {
                        if (accessoryRemoveSectionId) {
                            dispatch(removeProduct(currentBuildIndex, accessoryRemoveSectionId));
                        }
                    } else {
                        onRemove(product, e.target.form);
                    }
                }}
            >
                Remove piece
            </button>
        );
    }

    const productContainerClasses = classNames('product-main h-product', {
        'section-has-product': isSelected,
        'section-bodies': activeSection === 'bodies',
    });

    return (
        <div className={productContainerClasses}>
            <button type="button" className="pagetitle-back pagetitle-back-category" onClick={backButtonFunc}>
                Back
            </button>

            <div className="product-details">
                <h2 className="product-main-title">{product?.title?.split('-')[0]}</h2>
                <div className="product-price-container">
                    <span className="product-price">
                        <data className="p-price" value={productPrice?.amount || ''}>
                            <BuilderMoney amount={productPrice} className="product-price" />
                        </data>
                        {productStockDisplay}
                    </span>
                </div>
                <div className="product-details-inner">
                    <div className="product-form">
                        <form
                            action="/cart/add"
                            method="post"
                            encType="multipart/form-data"
                            id={`AddToCartForm-${prodIdParsed}`}
                            className="product-form"
                        >
                            {(activeSection === 'bodies' || reverseImage) &&
                                activeSection !== 'hatsAndHair' &&
                                !isKeyChain && (
                                    <div className="product-torso-switcher">
                                        <button
                                            className={`show-front ${!showBackImage ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowBackImage(false);
                                            }}
                                        >
                                            Front
                                        </button>
                                        <button
                                            className={`show-back ${showBackImage ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowBackImage(true);
                                            }}
                                        >
                                            Back
                                        </button>
                                    </div>
                                )}
                            {activeSection === 'hatsAndHair' ||
                            (activeSection === 'display' &&
                                attributesVars.length <= 1 &&
                                !getDisplayUploadConfig(product.id)) ? (
                                <Slideshow
                                    images={product.images?.edges || []}
                                    attr={attributes}
                                    videoUrls={videoUrls || []}
                                />
                            ) : (
                                <ProductImageView
                                    defaultImage={product.featuredImage}
                                    attr={attributes}
                                    backImageSrc={torsoBackImage || reverseImage?.reference?.image?.url}
                                    showBackImage={showBackImage}
                                    torsoOverlayImages={torsoOverlayImages}
                                    activeSection={activeSection}
                                    currentProductId={product.id}
                                    selectedProducts={currentBuildProducts}
                                    torsoTextState={torsoText}
                                    productTags={product.tags}
                                    product={product}
                                    variations={
                                        product.variants.nodes ? product.variants.nodes : product.variants.edges
                                    }
                                    isKeyChain={isKeyChain}
                                    headFeatures={headFeatures}
                                    torsoImageUpload={torsoImageUpload}
                                    displayImageUpload={displayImageUpload}
                                    displayImageUploadDraft={displayImageUploadDraft}
                                    addonsContainerRef={addonsContainerRef}
                                />
                            )}

                            <HeadBuilderOptions product={product} />

                            <div className="options-container">
                                <Attributes
                                    attributes={product.attributes}
                                    activeSection={activeSection}
                                    skintone={skintone}
                                    optionLabel={product?.options && product?.options[0]?.name}
                                    variations={
                                        product.variants.nodes ? product.variants.nodes : product.variants.edges
                                    }
                                    selected={dropdownDefaultValue}
                                    onChange={(attribute, value) => changeAttribute(attribute, value)}
                                />
                            </div>

                            <Addons
                                addons={product.product_attribute}
                                productId={prodIdParsed}
                                frontTextEventListenerExists={frontTextEventListenerExists}
                                backTextEventListenerExists={backTextEventListenerExists}
                                setBackTextEventListenerExists={(bool) => setBackTextEventListenerExists(bool)}
                                setFrontTextEventListenerExists={(bool) => setFrontTextEventListenerExists(bool)}
                                torsoTextState={torsoText}
                                setTorsoBackText={(backText) =>
                                    setTorsoTextState((prev) => ({
                                        ...prev,
                                        backText: backText,
                                    }))
                                }
                                setTorsoFrontText={(frontText) =>
                                    setTorsoTextState((prev) => ({
                                        ...prev,
                                        frontText: frontText,
                                    }))
                                }
                                sectionHasProduct={sectionHasProduct}
                                addonsContainerRef={addonsContainerRef}
                                disabled={isSelected}
                            />

                            <DoubleSidedHeadSelector
                                product={product}
                                selectedHeadProduct={selectedHeadProduct}
                                selectedKeychainProduct={selectedKeychainProduct}
                                activeSection={activeSection}
                            />

                            <input type="number" name="quantity" value="1" hidden readOnly />

                            <input
                                type="text"
                                id="projectTitle"
                                name="properties[projectTitle]"
                                value={projectTitle ? projectTitle : ''}
                                hidden
                                readOnly
                            />

                            {activeSection === 'bodies' && (
                                <>
                                    <input
                                        type="hidden"
                                        name={`properties[_overlayImage]`}
                                        id="overlayImageValue"
                                        value={torsoOverlayImages.torsoOverlayImage}
                                        readOnly
                                    />

                                    <input
                                        type="hidden"
                                        name={`properties[_overlayHands]`}
                                        id="overlayHands"
                                        value={torsoOverlayImages.handImage}
                                        readOnly
                                    />

                                    <div className="custom-arms-tip">
                                        There are custom arms available for this torso, find them in extras.
                                    </div>
                                </>
                            )}

                            <div
                                className="product-description-container"
                                onClick={needsTruncate ? () => setShowDescription(!showDescription) : undefined}
                                role={needsTruncate ? 'button' : undefined}
                                tabIndex={needsTruncate ? 0 : undefined}
                            >
                                {showDescription ? (
                                    <div
                                        className={`product-description expanded`}
                                        dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
                                    />
                                ) : (
                                    <div className={`product-description trimmed`}>{collapsedText}</div>
                                )}
                                {!showDescription && needsTruncate && (
                                    <svg
                                        className="icon icon-info"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="800px"
                                        height="800px"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M16.5303 8.96967C16.8232 9.26256 16.8232 9.73744 16.5303 10.0303L12.5303 14.0303C12.2374 14.3232 11.7626 14.3232 11.4697 14.0303L7.46967 10.0303C7.17678 9.73744 7.17678 9.26256 7.46967 8.96967C7.76256 8.67678 8.23744 8.67678 8.53033 8.96967L12 12.4393L15.4697 8.96967C15.7626 8.67678 16.2374 8.67678 16.5303 8.96967Z"
                                            fill="#000000"
                                        />
                                    </svg>
                                )}
                                <hr />
                            </div>

                            {!isInStock ? (
                                <p className="btn button span disabled soldout-1">
                                    <span>Sold out</span>
                                </p>
                            ) : (
                                button
                            )}
                            <p
                                className={classNames('product-copyright', {
                                    'product-copyright-expanded': showingCopyright,
                                })}
                                onClick={() => setShowingCopyright((prev) => !prev)}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
                                    <path d="M8 0a8 8 0 1 0 8 8 8 8 0 0 0-8-8Zm-.16 2.96a1.333 1.333 0 0 1 1.325 1.331A1.317 1.317 0 0 1 7.84 5.6a1.3 1.3 0 0 1-1.306-1.314A1.315 1.315 0 0 1 7.848 2.96Zm2.507 9.965a.115.115 0 0 1-.115.115H5.883a.115.115 0 0 1-.115-.115v-.64a.117.117 0 0 1 .115-.117h.9a.4.4 0 0 0 .114-.232 1.947 1.947 0 0 0 .014-.274V8.347c0-.48-.16-.64-.64-.661h-.613v-.564l3.6-.128v4.774a.48.48 0 0 0 .112.392h.87a.117.117 0 0 1 .115.117Z"></path>
                                </svg>
                                LEGO® is a trademark of the LEGO Groups of companies. The LEGO Group does not sponsor,
                                authorise or endorse this website nor any of the customised/modified products or sets
                                shown on it nor does it accept responsibility in any way, shape or form for any
                                unforeseen and/or adverse consequences following from such customisation/modification.
                            </p>
                        </form>
                    </div>
                </div>
            </div>

            {showReplaceItemWarning && formToSubmit && (
                <Warning
                    actionConfirm={() => {
                        setShowReplaceItemWarning(false);
                        submitProductForm(formToSubmit);
                    }}
                    warningText="Are you sure you want to make this change?"
                    actionDeny={() => {
                        setShowReplaceItemWarning(false);
                    }}
                />
            )}
            {showCompatWarning && (
                <Warning
                    title={compatWarningTitle}
                    warningText={`${compatWarningText}\n\nProceed anyway?`}
                    actionConfirm={() => {
                        const deferred = compatDeferredForm;
                        setShowCompatWarning(false);
                        setCompatWarningText('');
                        setCompatWarningTitle('');
                        setCompatDeferredForm(null);
                        if (deferred) submitProductForm(deferred, { skipCompatCheck: true });
                    }}
                    actionDeny={() => {
                        setShowCompatWarning(false);
                        setCompatWarningText('');
                        setCompatWarningTitle('');
                        setCompatDeferredForm(null);
                    }}
                />
            )}
        </div>
    );
};

Customise.propTypes = {
    // From redux
    activeSection: PropTypes.string,
    currentBuildIndex: PropTypes.number,
    skintone: PropTypes.string,
    currentBuildProducts: PropTypes.object,
    torsoReduxText: PropTypes.shape({
        frontText: PropTypes.string,
        backText: PropTypes.string,
    }),
    headFeatures: PropTypes.object,

    // Injected by connect
    dispatch: PropTypes.func,

    // Product object (GraphQL result shape simplified)
    product: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string,
        availableForSale: PropTypes.bool,
        tags: PropTypes.arrayOf(PropTypes.string),
        featuredImage: PropTypes.object,
        descriptionHtml: PropTypes.string,
        doubleSidedHeadTextLabels: PropTypes.shape({
            value: PropTypes.string,
        }),
        media: PropTypes.shape({
            edges: PropTypes.arrayOf(
                PropTypes.shape({
                    node: PropTypes.shape({
                        mediaContentType: PropTypes.string,
                        sources: PropTypes.arrayOf(
                            PropTypes.shape({
                                url: PropTypes.string,
                                mimeType: PropTypes.string,
                            })
                        ),
                    }),
                })
            ),
        }),
        images: PropTypes.shape({
            edges: PropTypes.arrayOf(
                PropTypes.shape({
                    node: PropTypes.shape({
                        url: PropTypes.string,
                    }),
                })
            ),
        }),
        variants: PropTypes.shape({
            nodes: PropTypes.arrayOf(PropTypes.object),
            edges: PropTypes.arrayOf(
                PropTypes.shape({
                    node: PropTypes.object,
                })
            ),
        }),
        attributes: PropTypes.arrayOf(PropTypes.object),
        options: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
                values: PropTypes.arrayOf(PropTypes.string),
            })
        ),
        product_attribute: PropTypes.array,
    }).isRequired,
    product_attribute: PropTypes.array,
    projectTitle: PropTypes.string,
    selected: PropTypes.object,
    onSelect: PropTypes.func,
    onRemove: PropTypes.func,
    backButtonFunc: PropTypes.func,
    showDescription: PropTypes.bool,
    setShowDescription: PropTypes.func,
    selectedProduct: PropTypes.object,
    torsoImageUpload: PropTypes.shape({
        compositeDataUri: PropTypes.string,
    }),
    displayImageUpload: PropTypes.shape({
        compositeDataUri: PropTypes.string,
        dataUri: PropTypes.string,
        productId: PropTypes.string,
    }),
    displayImageUploadDraft: PropTypes.shape({
        compositeDataUri: PropTypes.string,
        dataUri: PropTypes.string,
        productId: PropTypes.string,
    }),
};

const mapStateToProps = (state) => {
    return {
        currentBuildIndex: state.currentBuildIndex,
        activeSection: state.navigation.selected,
        skintone: state.builds[state.currentBuildIndex].skintone,
        currentBuildProducts: state.builds[state.currentBuildIndex].selected,
        torsoReduxText: state.builds[state.currentBuildIndex].torsoText,
        headFeatures: state.builds[state.currentBuildIndex].headFeatures,
        selectedProduct: state.browser.selectedProduct,
        torsoImageUpload: state.builds[state.currentBuildIndex].torsoImageUpload,
        displayImageUpload: state.builds[state.currentBuildIndex].displayImageUpload,
        displayImageUploadDraft: state.builds[state.currentBuildIndex].displayImageUploadDraft,
    };
};

export default connect(mapStateToProps)(Customise);
